import type { ReactNode, RefObject, FunctionComponent } from 'react';
import type { PopperPlacementType } from '@mui/material/Popper';
import Popper from './Popper';
import type { PopperMenuItemProps } from './PopperMenuItem';
import PopperMenuItem from './PopperMenuItem';

type PopperMenuProps = {
  children: ReactNode | ReactNode[];
  ellipsisDirection?: 'vertical' | 'horizontal';
  placement?: PopperPlacementType;
  modalContainer?: RefObject<HTMLElement>;
};

type PopperMenuComponent = FunctionComponent<PopperMenuProps> & {
  Item: FunctionComponent<PopperMenuItemProps>;
};

/**
 * Use the generic Popper to create a menu of clickable items.
 *
 * @param {PopperMenuProps} props - PopperMenuProps props
 * @param {ReactNode | ReactNode[]} props.children - The menu items, (usually PopperMenuItem components).
 * @param {'vertical' | 'horizontal'} [props.ellipsisDirection] - Direction of the ellipsis (default 'vertical').
 * @param {PopperPlacementType} [props.placement] - Placement of the Popper (see PopperPlacementType)
 * @param {RefObject<HTMLElement>} [props.modalContainer] - Ref to the modal container, which is required for Poppers to work on a modal (due to z-index issues).
 * @example
 * import PopperMenu from '../generic/PopperMenu';
 *
 * <PopperMenu>
 *   <PopperMenu.Item onClick={onViewRole}>
 *     View Role
 *   </PopperMenu.Item>
 *   <PopperMenu.Item onClick={onDeleteRole}>
 *     Delete Role
 *   </PopperMenu.Item>
 * </PopperMenu>
 */
const PopperMenu: PopperMenuComponent = ({
  children,
  ellipsisDirection = 'vertical',
  placement,
  modalContainer,
}) => (
  <Popper
    icon={ellipsisDirection === 'horizontal' ? 'ellipsisHorizontal' : 'ellipsisVertical'}
    placement={placement}
    modalContainer={modalContainer}
  >
    <nav className="fossa-popper-menu">
      <ul className="fossa-popper-menu__list">
        {children}
      </ul>
    </nav>
  </Popper>
);

//  This makes it possible to use PopperMenuItem without needing to import it directly (see @example above).
PopperMenu.Item = PopperMenuItem;

export default PopperMenu;
